import { FC, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, ClickAwayListener, InputBase, Paper, Popper, Typography } from '@mui/material';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import PeopleAltTwoToneIcon from '@mui/icons-material/PeopleAltTwoTone';
import LocalShippingTwoToneIcon from '@mui/icons-material/LocalShippingTwoTone';
import ReceiptLongTwoToneIcon from '@mui/icons-material/ReceiptLongTwoTone';
import ShoppingCartTwoToneIcon from '@mui/icons-material/ShoppingCartTwoTone';
import InventoryTwoToneIcon from '@mui/icons-material/Inventory2TwoTone';
import BadgeTwoToneIcon from '@mui/icons-material/BadgeTwoTone';
import ArrowForwardTwoToneIcon from '@mui/icons-material/ArrowForwardTwoTone';
import CurrencyRupeeTwoToneIcon from '@mui/icons-material/CurrencyRupeeTwoTone';
import ReceiptTwoToneIcon from '@mui/icons-material/ReceiptTwoTone';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';
import { useModuleAccess } from '../../hooks/useModuleAccess';

const MAX_RESULTS = 10;
const MAX_PAGES = 4;

type ResultType = 'page' | 'buyer' | 'supplier' | 'sale' | 'purchase' | 'item' | 'employee' | 'paymentIn' | 'paymentOut' | 'expense';

interface SearchResult {
  type: ResultType;
  id: string;
  title: string;
  subtitle?: string;
  templateId?: string;
  voucherCategory?: string;
  path?: string;
}

// App pages and reports; `module` hides entries the user can't open (null = everyone).
const PAGES: { title: string; path: string; module: string | null; group?: string }[] = [
  { title: 'Dashboard', path: '/home', module: null },
  { title: 'Sales', path: '/sales', module: 'sales' },
  { title: 'New Sales Invoice', path: '/sales/invoice/gst_invoice/create', module: 'sales' },
  { title: 'Credit Notes', path: '/sales/credit-note', module: 'sales' },
  { title: 'Purchases', path: '/purchases', module: 'purchases' },
  { title: 'Payments', path: '/payments', module: 'payments' },
  { title: 'Expenses', path: '/expenses', module: 'expenses' },
  { title: 'Buyers', path: '/buyers', module: 'buyers' },
  { title: 'Suppliers', path: '/suppliers', module: 'suppliers' },
  { title: 'Inventory', path: '/inventory', module: 'inventory' },
  { title: 'People', path: '/people', module: 'employee' },
  { title: 'Reports', path: '/reports', module: 'reports' },
  { title: 'Chart of Accounts', path: '/coa', module: 'coa' },
  { title: 'Members', path: '/users', module: 'members' },
  { title: 'Settings', path: '/settings', module: 'settings' },
  { title: 'My Profile', path: '/my-profile', module: null },
  ...[
    ['GSTR-1 (Sales)', 'gstr-1-sales'], ['GSTR-2 (Purchase)', 'gstr-2-purchase'], ['Day Book', 'day-book'],
    ['Profit & Loss', 'profit-loss'], ['Balance Sheet', 'balance-sheet'], ['Balance Sheet (Party Wise)', 'balance-sheet-party-wise'],
    ['Party Ledger', 'party-ledger'], ['Party Wise Outstanding', 'party-wise-outstanding'], ['Party Balances', 'party-balances'],
    ['TDS Payable', 'tds-payable'], ['TDS Receivable', 'tds-receivables'], ['TCS Payable', 'tcs-payable'], ['TCS Receivable', 'tcs-receivables'],
    ['Low Stock Summary', 'low-stock-summary'], ['Stock Expiry Report', 'stock-expiry-report'], ['Rate List', 'rate-list'],
    ['Custom Item Report', 'custom-item-report'], ['Item Report by Party', 'item-report-by-party'], ['Item Purchase Summary', 'item-purchase-summary'],
    ['Item Sales & Purchase Summary', 'item-sales-and-purchase-summary'], ['Cash & Bank Report', 'cash-and-bank-report-all-payments'],
    ['Expense Category Report', 'expense-category-report'], ['Cost Centre Report', 'cost-centre-report'], ['Employee Ledger', 'employee-ledger'],
  ].map(([title, slug]) => ({ title, path: `/reports/${slug}`, module: 'reports', group: 'Report' })),
];

const TYPE_META: Record<ResultType, { label: string; icon: JSX.Element; color: string }> = {
  page: { label: 'Page', icon: <ArrowForwardTwoToneIcon fontSize="small" />, color: '#6b7280' },
  buyer: { label: 'Buyer', icon: <PeopleAltTwoToneIcon fontSize="small" />, color: '#2563eb' },
  supplier: { label: 'Supplier', icon: <LocalShippingTwoToneIcon fontSize="small" />, color: '#7c3aed' },
  sale: { label: 'Sales', icon: <ReceiptLongTwoToneIcon fontSize="small" />, color: '#059669' },
  purchase: { label: 'Purchase', icon: <ShoppingCartTwoToneIcon fontSize="small" />, color: '#d97706' },
  item: { label: 'Item', icon: <InventoryTwoToneIcon fontSize="small" />, color: '#0891b2' },
  employee: { label: 'Employee', icon: <BadgeTwoToneIcon fontSize="small" />, color: '#db2777' },
  paymentIn: { label: 'Payment In', icon: <CurrencyRupeeTwoToneIcon fontSize="small" />, color: '#16a34a' },
  paymentOut: { label: 'Payment Out', icon: <CurrencyRupeeTwoToneIcon fontSize="small" />, color: '#dc2626' },
  expense: { label: 'Expense', icon: <ReceiptTwoToneIcon fontSize="small" />, color: '#ea580c' },
};

const pathOf = (r: SearchResult) => {
  switch (r.type) {
    case 'page': return r.path || '/home';
    case 'buyer':
    case 'supplier': return `/customer/view/${r.id}`;
    case 'sale':
    case 'purchase': return `/invoice/${r.id}/preview?templateId=${r.templateId || 'DEFAULT'}&type=${r.voucherCategory === 'purchase' ? 'purchase' : 'sales'}`;
    case 'item': return `/inventory/view/${r.id}`;
    case 'employee': return `/people/employee/view/${r.id}`;
    // No detail page: open the list with this record pinned on top.
    case 'paymentIn': return `/payments?tab=in&pin=${r.id}`;
    case 'paymentOut': return `/payments?tab=out&pin=${r.id}`;
    case 'expense': return `/expenses?pin=${r.id}`;
  }
};

// Header search: pages, reports and records (parties, vouchers, items, employees), top 10.
const GlobalSearch: FC = () => {
  const navigate = useNavigate();
  const { canAccessModule } = useModuleAccess();
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const requestId = useRef(0);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<SearchResult[]>([]);
  const [active, setActive] = useState(0);

  const q = query.trim().toLowerCase();

  const pages: SearchResult[] = useMemo(() => !q ? [] : PAGES
    .filter((p) => (!p.module || canAccessModule(p.module)) && p.title.toLowerCase().includes(q))
    .sort((a, b) => Number(!a.title.toLowerCase().startsWith(q)) - Number(!b.title.toLowerCase().startsWith(q)))
    .slice(0, MAX_PAGES)
    .map((p) => ({ type: 'page', id: p.path, title: p.title, subtitle: p.group || 'Page', path: p.path })), [q, canAccessModule]);

  const results = [...pages, ...records].slice(0, MAX_RESULTS);

  // Debounced record search; the latest request wins.
  useEffect(() => {
    if (q.length < 2) { setRecords([]); setLoading(false); return; }
    const id = ++requestId.current;
    setLoading(true);
    const timer = setTimeout(() => {
      axios.get(`/api/v1/search?q=${encodeURIComponent(query.trim())}`)
        .then((res: any) => { if (id === requestId.current) setRecords(res.data?.results || []); })
        .catch(() => { if (id === requestId.current) setRecords([]); })
        .finally(() => { if (id === requestId.current) setLoading(false); });
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useEffect(() => { setActive(0); }, [q, records]);

  // Ctrl/Cmd + K focuses search from anywhere.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const go = (r: SearchResult) => {
    navigate(pathOf(r));
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && results[active]) { e.preventDefault(); go(results[active]); }
    else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
  };

  const showPanel = open && q.length > 0;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box
        ref={anchorRef}
        sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, height: 39.5, width: 340, px: 1.25, bgcolor: '#fff', borderRadius: '25px', float: 'right', mt: '-5px', mr: '15px' }}
      >
        <SearchTwoToneIcon sx={{ fontSize: 24, color: '#545454' }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search Anything..."
          sx={{ flex: 1, fontSize: 16 }}
        />
        {loading && <CircularProgress size={16} />}
        {query
          ? <IconButton size="small" aria-label="Clear search" onMouseDown={(e) => e.preventDefault()} onClick={() => { setQuery(''); setRecords([]); inputRef.current?.focus(); }} sx={{ p: 0.25, color: '#9ca3af' }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          : <Typography sx={{ fontSize: 11, color: '#9ca3af', border: '1px solid #e5e7eb', borderRadius: '6px', px: 0.75, lineHeight: '18px' }}>Ctrl+K</Typography>}

        <Popper open={showPanel} anchorEl={anchorRef.current} placement="bottom-start" style={{ zIndex: 1400, width: 420 }}>
          <Paper elevation={8} sx={{ mt: 1, borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            {results.length === 0 ? (
              <Typography sx={{ p: 2, fontSize: 13, color: '#6b7280' }}>
                {loading || q.length < 2 ? 'Searching…' : `No results for "${query.trim()}"`}
              </Typography>
            ) : (
              <Box sx={{ py: 0.5 }}>
                {results.map((r, i) => {
                  const meta = TYPE_META[r.type];
                  return (
                    <Box
                      key={`${r.type}-${r.id}`}
                      onMouseEnter={() => setActive(i)}
                      onMouseDown={(e) => { e.preventDefault(); go(r); }}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, cursor: 'pointer', bgcolor: i === active ? '#f3f4f6' : 'transparent' }}
                    >
                      <Box sx={{ width: 30, height: 30, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: meta.color, bgcolor: `${meta.color}14`, flexShrink: 0 }}>
                        {meta.icon}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: '#111827' }}>{r.title}</Typography>
                        {r.subtitle && <Typography noWrap sx={{ fontSize: 11.5, color: '#6b7280' }}>{r.subtitle}</Typography>}
                      </Box>
                      <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: meta.color, flexShrink: 0 }}>{meta.label}</Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
            <Box sx={{ px: 1.5, py: 0.75, borderTop: '1px solid #f0f0f0', bgcolor: '#fafafa', display: 'flex', gap: 2 }}>
              {['↑↓ navigate', '↵ open', 'esc close'].map((hint) => (
                <Typography key={hint} sx={{ fontSize: 10.5, color: '#9ca3af' }}>{hint}</Typography>
              ))}
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
